import { Injectable } from '@angular/core';
import { SideMenuItem } from '@flux/design-system';
import { FeatureFlagsEnum } from '@generated/featureFlags.enum';
import { GlobalPermissionsEnum, PermissionsEnum } from '@generated/permissions.enum';

/**
 * Extended SideMenuItem with permission and feature flag support
 */
export interface NavigationMenuItem extends Omit<SideMenuItem, 'hidden' | 'children'> {
  children?: NavigationMenuItem[];
  has_feature_flag?: `${FeatureFlagsEnum}`[] | FeatureFlagsEnum[];
  required_permissions?: GlobalPermissionsEnum[] | `${GlobalPermissionsEnum}`[];
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private navigations: NavigationMenuItem[] = [
    {
      id: 'chat',
      title: 'Pergunte à Iara',
      url: 'chat',
      icon: { unselected: 'chat-outline', selected: 'chat-filled' },
      iconVariant: 'iara-orb',
      has_feature_flag: [FeatureFlagsEnum.FeatureChatAi],
      isBeta: true,
    },
    {
      id: 'meu-espaco',
      title: 'Meu espaço',
      url: 'meu-espaco',
      icon: { unselected: 'home-outline', selected: 'home-filled' },
    },
    {
      id: 'fluxos',
      title: 'Fluxos de trabalho',
      url: 'fluxos',
      icon: { unselected: 'view-kanban-outline', selected: 'view-kanban-filled' },
      required_permissions: [
        PermissionsEnum.GlobalPipesReadAll,
        PermissionsEnum.GlobalPipesReadLimited,
      ],
    },

    {
      id: 'banco-de-dados',
      title: 'Banco de dados',
      url: 'banco-de-dados',
      icon: { unselected: 'database-outline', selected: 'database-filled' },
      has_feature_flag: [FeatureFlagsEnum.FeatureDatabase],
      required_permissions: [
        PermissionsEnum.GlobalDatabaseReadAll,
        PermissionsEnum.GlobalDatabaseReadLimited,
      ],
    },
    {
      id: 'analytics',
      title: 'Analytics',
      url: 'analytics',
      icon: { unselected: 'bar-chart-outline', selected: 'insert-chart-outline' },
      has_feature_flag: [FeatureFlagsEnum.FeatureAnalytics],
      required_permissions: [PermissionsEnum.GlobalReportsRead],
      isBeta: true,
    },
    {
      id: 'agentes',
      title: 'Agentes',
      url: 'agentes',
      icon: { unselected: 'robot-2-outline', selected: 'robot-2-filled' },
      has_feature_flag: [FeatureFlagsEnum.FeatureAgentBuilder],
      // A lista geral (Agent Builder) é restrita a quem gerencia agentes.
      // Membros comuns mantêm read_own/execute apenas para usar agentes no
      // chat e nas automações, sem acesso ao hub de construção.
      required_permissions: [
        PermissionsEnum.GlobalAgentsReadAll,
        PermissionsEnum.GlobalAgentsCreate,
        PermissionsEnum.GlobalAgentsUpdate,
        PermissionsEnum.GlobalAgentsUpdateOwn,
        PermissionsEnum.GlobalAgentsDelete,
      ],
      isBeta: true,
    },
    {
      id: 'configuracoes',
      title: 'Configurações',
      withDivider: true,
      children: [
        {
          id: 'gerenciar-papeis',
          title: 'Papéis',
          url: 'gerenciar/papeis',
          icon: { unselected: 'shield-outline', selected: 'shield-filled' },
          withDivider: true,
          required_permissions: [PermissionsEnum.GlobalRolesManagePermissions],
        },
        {
          id: 'gerenciar-pessoas',
          title: 'Pessoas',
          url: 'gerenciar/pessoas',
          icon: { unselected: 'peoples-outline', selected: 'peoples-filled' },
          required_permissions: [PermissionsEnum.GlobalRolesManageUsers],
        },
        {
          id: 'gerenciar-times',
          title: 'Times',
          url: 'gerenciar/times',
          has_feature_flag: [FeatureFlagsEnum.FeatureTeams],
          icon: { unselected: 'peoples-outline', selected: 'peoples-filled' },
          required_permissions: [PermissionsEnum.GlobalTeamsManageUsers],
        },
        {
          id: 'gerenciar-variaveis',
          title: 'Variáveis customizadas',
          url: 'gerenciar/variaveis',
          icon: { unselected: 'code-outline', selected: 'code-outline' },
          has_feature_flag: [FeatureFlagsEnum.FeatureCustomVariables],
          required_permissions: [PermissionsEnum.GlobalVariablesRead],
        },
        {
          id: 'gerenciar-contas-de-servico',
          title: 'Contas de serviço',
          url: 'gerenciar/contas-de-servico',
          icon: { unselected: 'robot-outline', selected: 'robot-filled' },
          has_feature_flag: [FeatureFlagsEnum.FeatureServiceAccounts],
          required_permissions: [PermissionsEnum.GlobalServiceAccountsRead],
        },
        {
          id: 'ausencias',
          title: 'Ausências',
          url: 'ausencias',
          icon: { unselected: 'partner-exchange-outline', selected: 'partner-exchange-filled' },
          has_feature_flag: [FeatureFlagsEnum.UserDelegations],
        },
        {
          id: 'auditoria',
          title: 'Registros de auditoria',
          url: 'auditoria',
          icon: { unselected: 'history-outline', selected: 'history-outline' },
          required_permissions: [PermissionsEnum.GlobalAuditLogsRead],
        },
        {
          id: 'gerenciar-faturamento',
          title: 'Faturamento',
          url: 'gerenciar/faturamento',
          icon: { unselected: 'finance-outline', selected: 'finance-filled' },
          required_permissions: [PermissionsEnum.GlobalFeaturesManage],
        },
        {
          id: 'consumo-ia',
          title: 'Consumo de IA',
          url: 'gerenciar/consumo-ia',
          icon: { unselected: 'auto-awesome-outlined', selected: 'auto-awesome-outlined' },
          has_feature_flag: [FeatureFlagsEnum.FeatureChatAi],
          required_permissions: [PermissionsEnum.GlobalIaraCreditsRead],
        },
      ],
      icon: { unselected: 'settings-outline', selected: 'settings-filled' },
    },
  ];

  getNavigationItems(): NavigationMenuItem[] {
    return this.navigations;
  }
}
